/**
 * The POS master data half of the Effort Matrix screen (R4.2, R4.3, R9.4).
 *
 * Two cards side by side: the OneData-sourced POS_Segment counts every derived
 * population figure comes from, and the manual overrides that replaced one of those
 * figures on a specific Initiative.
 *
 * ## Why they sit on this screen at all
 *
 * The Effort Matrix answers "how long does one unit of work take". The POS counts
 * answer "how many units are there". A Demand_Hours figure is the product of the two,
 * so a reader auditing one without the other can only check half of it — and R4.2
 * requires the source timestamp to be displayed wherever a derived count is, which
 * makes this the screen that owes it.
 *
 * ## The override table shows both numbers
 *
 * R4.3: a manual count that replaces a POS_Segment-derived one must be marked, carry
 * a reason, and *retain and display the derived value alongside* rather than
 * replacing it. An override whose original is invisible cannot be reviewed, so both
 * columns are present and the override is the one that is tinted.
 */
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/shared/format/date-format';
import type { ManpowerReferenceRecord } from '../manpower-reference-data';
import { RoleBadge } from '../shared/RoleBadge';
import type { PopulationOverride } from './pos-master-data';

export interface PosMasterDataSectionProps {
  readonly segments: readonly ManpowerReferenceRecord[];
  readonly segmentsPending: boolean;
  readonly overrides: readonly PopulationOverride[];
  readonly overridesPending: boolean;
  readonly overridesError: boolean;
}

export function PosMasterDataSection({
  segments,
  segmentsPending,
  overrides,
  overridesPending,
  overridesError,
}: PosMasterDataSectionProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="em-pos-heading" className="flex flex-col gap-2">
      <h2
        id="em-pos-heading"
        className="text-label text-[10.5px] font-semibold tracking-wide uppercase"
      >
        {t('manpower.effortMatrix.pos.heading')}
      </h2>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <SegmentsCard segments={segments} isPending={segmentsPending} />
        <OverridesCard
          overrides={overrides}
          isPending={overridesPending}
          isError={overridesError}
        />
      </div>
    </section>
  );
}

/** The POS_Segment counts and when each was sourced (R4.2, R9.4). */
function SegmentsCard({
  segments,
  isPending,
}: {
  readonly segments: readonly ManpowerReferenceRecord[];
  readonly isPending: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  /**
   * The newest source timestamp across the segments, for the card's refresh line.
   *
   * The newest rather than a single global one because each segment carries its own:
   * an import that updated four of five segments should not be reported as having
   * refreshed the fifth. The per-row timestamps below are what make that visible.
   */
  const latest = segments.reduce<string | null>((newest, segment) => {
    const stamp = segment.sourceTimestamp;
    if (stamp === null) {
      return newest;
    }
    return newest === null || stamp > newest ? stamp : newest;
  }, null);

  return (
    <Card className="gap-1 p-5">
      <p className="text-txt text-[13px] font-semibold">
        {t('manpower.effortMatrix.pos.segmentsHeading')}
      </p>
      <p className="text-muted-foreground mb-2 text-[11.5px]">
        {latest === null
          ? t('manpower.effortMatrix.pos.noSourceRefresh')
          : t('manpower.effortMatrix.pos.sourceRefresh', { timestamp: formatDateTime(latest) })}
      </p>

      {isPending ? (
        <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
      ) : segments.length === 0 ? (
        // Assumption A8: the table ships before the OneData import, so an empty
        // register is the expected first state rather than a fault.
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.effortMatrix.pos.segmentsEmpty')}
        </p>
      ) : (
        <Table>
          <caption className="sr-only">{t('manpower.effortMatrix.pos.segmentsCaption')}</caption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col" className="text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colSegment')}
              </TableHead>
              <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colPosCount')}
              </TableHead>
              <TableHead scope="col" className="text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colSourceTimestamp')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.map((segment) => (
              <TableRow key={segment.id}>
                <TableCell className="text-[12.3px]">
                  {segment.name}
                  {!segment.isActive && (
                    <span className="text-muted-foreground ml-2 text-[11px]">
                      {t('manpower.effortMatrix.deactivated')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right text-[12.3px] font-medium tabular-nums">
                  {/* An em dash, not a zero: A8 makes an unpopulated count distinct
                      from a genuinely zero one, and the whole point of the nullable
                      column is that the two must not be conflated. */}
                  {segment.posCount === null
                    ? t('manpower.absent')
                    : segment.posCount.toLocaleString()}
                </TableCell>
                <TableCell className="text-accent-line text-[12.3px] tabular-nums">
                  {segment.sourceTimestamp === null
                    ? t('manpower.absent')
                    : formatDateTime(segment.sourceTimestamp)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

/** The manual population overrides in force, each beside the value it replaced (R4.3). */
function OverridesCard({
  overrides,
  isPending,
  isError,
}: {
  readonly overrides: readonly PopulationOverride[];
  readonly isPending: boolean;
  readonly isError: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="gap-1 p-5">
      <p className="text-txt text-[13px] font-semibold">
        {t('manpower.effortMatrix.pos.overridesHeading')}
      </p>
      <p className="text-muted-foreground mb-2 text-[11.5px]">
        {t('manpower.effortMatrix.pos.overridesSubtitle')}
      </p>

      {isError ? (
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.effortMatrix.pos.overridesError')}
        </p>
      ) : isPending ? (
        <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
      ) : overrides.length === 0 ? (
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.effortMatrix.pos.overridesEmpty')}
        </p>
      ) : (
        <Table>
          <caption className="sr-only">{t('manpower.effortMatrix.pos.overridesCaption')}</caption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col" className="text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colInitiative')}
              </TableHead>
              <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colDerived')}
              </TableHead>
              <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colOverride')}
              </TableHead>
              <TableHead scope="col" className="text-[10.5px] uppercase">
                {t('manpower.effortMatrix.pos.colReason')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overrides.map((override) => (
              <TableRow key={override.driverId}>
                <TableCell className="max-w-[200px] text-[12.3px]" title={override.title}>
                  <span className="flex items-center gap-1.5">
                    <RoleBadge role={override.ffRole} />
                    <span className="truncate">{override.title}</span>
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground text-right text-[12.3px] tabular-nums">
                  {/* Retained, not replaced (R4.3). Muted because it is the value no
                      longer in force — present so the override can be judged. */}
                  {override.derivedCount === null
                    ? t('manpower.absent')
                    : override.derivedCount.toLocaleString()}
                </TableCell>
                <TableCell className="text-right text-[12.3px]">
                  {/* UI spec §3.4's override badge: the same amber pill the intake
                      form marks an overridden population with, so the two screens
                      agree on what "this number was entered by hand" looks like. */}
                  <span className="inline-flex w-fit items-center rounded-[var(--radius-badge)] bg-[#FDF0DC] px-1.5 py-px text-[11px] font-semibold tabular-nums text-[#9A5B00]">
                    {override.overrideCount.toLocaleString()}
                  </span>
                </TableCell>
                <TableCell
                  className="text-muted-foreground max-w-[220px] truncate text-[12.3px]"
                  title={override.reason}
                >
                  {override.reason}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <p className="text-muted-foreground mt-2 text-[11px] italic">
        {t('manpower.effortMatrix.pos.calculationNote')}
      </p>
    </Card>
  );
}
